import { useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { ScrollText, ImagePlus, RotateCcw, Lock, Loader2, Sparkles, Copy, Check, Volume2, Square, MessageCircle, ListChecks, Languages, PenLine, Compass, Expand } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { getVoterId } from "@/lib/voter";
import { CinematicVideo } from "@/components/CinematicVideo";

const SCRIBE_VIDEO =
  "https://customer-assets-7cd3h4nn.emergentagent.net/job_epiphany-mentor/artifacts/061a7dmb_gemini_generated_video_3ed31978.mp4";

export const ScribeSection = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const audioRef = useRef(null);
  const [preview, setPreview] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | done | limit | error
  const [text, setText] = useState("");
  const [remaining, setRemaining] = useState(null);
  const [unlimited, setUnlimited] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [copied, setCopied] = useState(false);
  const [summary, setSummary] = useState(null);
  const [summarizing, setSummarizing] = useState(false);
  const [translation, setTranslation] = useState(null);
  const [translating, setTranslating] = useState(false);
  const [showLangs, setShowLangs] = useState(false);
  const [audioState, setAudioState] = useState("idle"); // idle | loading | playing
  const [transform, setTransform] = useState(null); // { text, mode }
  const [transforming, setTransforming] = useState(null); // mode string | null

  const LANGS = [["fr", "Français"], ["en", "English"], ["es", "Español"], ["nl", "Nederlands"]];

  const pick = () => inputRef.current?.click();

  const stopAudio = () => {
    if (audioRef.current) { audioRef.current.pause(); audioRef.current = null; }
    setAudioState("idle");
  };

  const reset = () => {
    stopAudio();
    setPreview((p) => { if (p) URL.revokeObjectURL(p); return ""; });
    setStatus("idle"); setText(""); setErrMsg(""); setSummary(null); setCopied(false);
    setTranslation(null); setShowLangs(false); setTransform(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const compressImage = (file) => new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      const maxDim = 3000;
      let { width, height } = img;
      if (Math.max(width, height) > maxDim) {
        const r = maxDim / Math.max(width, height);
        width = Math.round(width * r); height = Math.round(height * r);
      }
      const canvas = document.createElement("canvas");
      canvas.width = width; canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      canvas.toBlob((blob) => resolve(blob || file), "image/jpeg", 0.9);
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("decode")); };
    img.src = url;
  });

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type && !file.type.startsWith("image/")) {
      setStatus("error"); setErrMsg(t("scribe.formatError")); return;
    }
    setStatus("loading"); setText(""); setErrMsg(""); setSummary(null); setTranslation(null); setShowLangs(false); setTransform(null);
    let blob;
    try { blob = await compressImage(file); }
    catch { setStatus("error"); setErrMsg(t("scribe.formatError")); return; }
    if (blob.size > 20 * 1024 * 1024) {
      setStatus("error"); setErrMsg(t("scribe.sizeError")); return;
    }
    setPreview((p) => { if (p) URL.revokeObjectURL(p); return URL.createObjectURL(blob); });
    const fd = new FormData();
    fd.append("image", blob, "scan.jpg");
    fd.append("voter_id", getVoterId());
    fd.append("lang", i18n.language || "fr");
    try {
      const { data } = await api.post("/scribe/transcribe", fd);
      setText((data.text || "").replace(/\n{3,}/g, "\n\n").trim());
      setRemaining(data.remaining);
      setUnlimited(!!data.unlimited);
      setStatus("done");
    } catch (err) {
      if (err.response?.status === 402) setStatus("limit");
      else { setStatus("error"); setErrMsg(formatApiErrorDetail(err.response?.data?.detail)); }
    }
  };

  const copyText = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true); toast.success(t("scribe.copied"));
      setTimeout(() => setCopied(false), 1800);
    } catch { toast.error(t("scribe.error")); }
  };

  const doSummarize = async () => {
    if (summarizing) return;
    setSummarizing(true);
    try {
      const { data } = await api.post("/scribe/summarize", { text, lang: i18n.language || "fr" });
      setSummary(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("scribe.error"));
    } finally { setSummarizing(false); }
  };

  const doTranslate = async (target) => {
    setShowLangs(false);
    if (translating) return;
    setTranslating(true);
    try {
      const { data } = await api.post("/scribe/translate", { text, target });
      setTranslation({ text: data.text, target });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("scribe.error"));
    } finally { setTranslating(false); }
  };

  const doListen = async () => {
    if (audioState === "playing" || audioState === "loading") { stopAudio(); return; }
    setAudioState("loading");
    try {
      const { data } = await api.post("/scribe/listen", { text, lang: i18n.language || "fr" });
      const audio = new Audio(`data:audio/mpeg;base64,${data.audio}`);
      audioRef.current = audio;
      audio.onended = () => setAudioState("idle");
      await audio.play();
      setAudioState("playing");
    } catch (err) {
      setAudioState("idle");
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("scribe.error"));
    }
  };

  const doTransform = async (mode) => {
    if (transforming) return;
    setTransforming(mode);
    try {
      const { data } = await api.post("/scribe/transform", { text, lang: i18n.language || "fr", mode });
      setTransform({ text: data.text, mode });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || t("scribe.error"));
    } finally { setTransforming(null); }
  };

  const discuss = () => {
    localStorage.setItem("coccon_pending_prompt", t("scribe.discussPrompt", { text: text.slice(0, 1500) }));
    navigate("/atelier");
  };

  return (
    <section className="relative px-6 my-20" data-testid="scribe">
      <div className="max-w-4xl mx-auto">
        <div className="relative rounded-[2rem] overflow-hidden border border-primary/20 frame-pulse glass">
          <div className="relative z-10 px-6 sm:px-12 py-14 text-center">
            <div className="max-w-2xl mx-auto mb-10">
              <CinematicVideo
                silver
                src={SCRIBE_VIDEO}
                testId="scribe-cinematic-video"
                poster="/scribe-poster.jpg"
                caption={t("scribe.subtitle")}
                fade
                noSound
              />
            </div>
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/15 border border-primary/40 text-primary text-[11px] uppercase tracking-[0.28em] mb-6" data-testid="scribe-badge">
              <ScrollText size={13} /> {t("scribe.badge")}
            </span>
            <h2 className="font-display text-gradient-orange text-4xl sm:text-6xl leading-[1.12] pb-[0.08em] mb-4" data-testid="scribe-title">
              {t("scribe.title")}
            </h2>
            <p className="text-foreground/65 text-base sm:text-lg max-w-xl mx-auto leading-relaxed mb-9">
              {t("scribe.subtitle")}
            </p>

            <input ref={inputRef} type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="scribe-file-input" />

            <AnimatePresence mode="wait">
              {status === "idle" && (
                <motion.div key="idle" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
                  <button onClick={pick} className="btn-frost-orange inline-flex items-center gap-3 px-8 py-4 rounded-full font-semibold text-base" data-testid="scribe-upload-btn">
                    <ImagePlus size={20} /> {t("scribe.cta")}
                  </button>
                  <p className="text-foreground/40 text-sm mt-5 italic">{t("scribe.hint")}</p>
                </motion.div>
              )}

              {status === "loading" && (
                <motion.div key="loading" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col items-center" data-testid="scribe-loading">
                  <div className="relative w-52 h-52 rounded-2xl overflow-hidden mb-6 border border-primary/20">
                    {preview && <img src={preview} alt="" className="w-full h-full object-cover opacity-70" />}
                    <div className="absolute inset-0 flex items-center justify-center bg-background/40">
                      <Loader2 size={34} className="text-primary animate-spin" />
                    </div>
                  </div>
                  <p className="text-primary/80 text-sm uppercase tracking-[0.25em] animate-pulse">{t("scribe.reading")}</p>
                </motion.div>
              )}

              {status === "done" && (
                <motion.div key="done" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <div className="flex flex-col sm:flex-row items-start gap-5 text-left">
                    {preview && (
                      <img src={preview} alt="" className="w-24 h-24 rounded-2xl object-cover border border-primary/30 flex-shrink-0 mx-auto sm:mx-0" data-testid="scribe-thumb" />
                    )}
                    <div className="flex-1 w-full min-w-0">
                      <div className="relative">
                        <div className="glass rounded-2xl border border-white/10 p-5 max-h-72 overflow-y-auto whitespace-pre-wrap text-foreground/85 text-[15px] leading-relaxed font-display" data-testid="scribe-text">
                          {text}
                        </div>
                        <button onClick={copyText} className="absolute top-3 right-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-background/70 border border-white/10 text-foreground/60 hover:text-primary text-xs transition-colors" data-testid="scribe-copy-btn">
                          {copied ? <Check size={13} /> : <Copy size={13} />} {copied ? t("scribe.copied") : t("scribe.copy")}
                        </button>
                      </div>
                    </div>
                  </div>

                  <AnimatePresence>
                    {summary && (
                      <motion.div key="summary" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
                        className="mt-5 glass rounded-2xl border border-primary/20 p-5 text-left overflow-hidden" data-testid="scribe-summary">
                        <h3 className="font-display text-primary/90 text-lg mb-3 flex items-center gap-2"><Sparkles size={16} /> {t("scribe.summaryTitle")}</h3>
                        <ul className="space-y-2 mb-4">
                          {summary.ideas.map((idea, i) => (
                            <li key={i} className="flex items-start gap-3 text-foreground/75 text-[15px] leading-relaxed">
                              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/15 text-primary flex items-center justify-center font-display text-xs">{i + 1}</span>{idea}
                            </li>
                          ))}
                        </ul>
                        {summary.question && (
                          <p className="text-foreground/60 italic text-sm border-t border-white/10 pt-3">
                            <span className="text-primary/70 not-italic font-semibold">{t("scribe.summaryQuestion")} · </span>{summary.question}
                          </p>
                        )}
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <AnimatePresence>
                    {translation && (
                      <motion.div key="translation" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
                        className="mt-5 glass rounded-2xl border border-primary/20 p-5 text-left overflow-hidden" data-testid="scribe-translation">
                        <h3 className="font-display text-primary/90 text-lg mb-3 flex items-center gap-2">
                          <Languages size={16} /> {t("scribe.translationTitle")}
                          <span className="text-foreground/40 text-xs font-normal uppercase tracking-wider">· {translation.target}</span>
                        </h3>
                        <p className="whitespace-pre-wrap text-foreground/85 text-[15px] leading-relaxed font-display max-h-72 overflow-y-auto">{translation.text}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <AnimatePresence>
                    {transform && (
                      <motion.div key="transform" initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
                        className="mt-5 glass rounded-2xl border border-primary/20 p-5 text-left overflow-hidden" data-testid="scribe-transform">
                        <h3 className="font-display text-primary/90 text-lg mb-3 flex items-center gap-2">
                          {transform.mode === "reformulate" ? <PenLine size={16} /> : transform.mode === "contextualize" ? <Compass size={16} /> : <Expand size={16} />}
                          {t(`scribe.${transform.mode}Title`)}
                        </h3>
                        <p className="whitespace-pre-wrap text-foreground/85 text-[15px] leading-relaxed font-display max-h-72 overflow-y-auto">{transform.text}</p>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <div className="mt-7 max-w-xl mx-auto space-y-3">
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      <button onClick={doSummarize} disabled={summarizing} className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-primary/40 text-primary hover:bg-primary/10 transition-colors font-semibold text-sm disabled:opacity-60 w-full" data-testid="scribe-summarize-btn">
                        {summarizing ? <Loader2 size={15} className="animate-spin" /> : <ListChecks size={15} />} {t("scribe.summarize")}
                      </button>
                      <button onClick={() => setShowLangs((v) => !v)} disabled={translating} className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-primary/40 text-primary hover:bg-primary/10 transition-colors font-semibold text-sm disabled:opacity-60 w-full" data-testid="scribe-translate-btn">
                        {translating ? <Loader2 size={15} className="animate-spin" /> : <Languages size={15} />} {t("scribe.translate")}
                      </button>
                      <button onClick={() => doTransform("reformulate")} disabled={!!transforming} className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-primary/40 text-primary hover:bg-primary/10 transition-colors font-semibold text-sm disabled:opacity-60 w-full" data-testid="scribe-reformulate-btn">
                        {transforming === "reformulate" ? <Loader2 size={15} className="animate-spin" /> : <PenLine size={15} />} {t("scribe.reformulate")}
                      </button>
                      <button onClick={() => doTransform("contextualize")} disabled={!!transforming} className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-primary/40 text-primary hover:bg-primary/10 transition-colors font-semibold text-sm disabled:opacity-60 w-full" data-testid="scribe-contextualize-btn">
                        {transforming === "contextualize" ? <Loader2 size={15} className="animate-spin" /> : <Compass size={15} />} {t("scribe.contextualize")}
                      </button>
                      <button onClick={() => doTransform("develop")} disabled={!!transforming} className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-primary/40 text-primary hover:bg-primary/10 transition-colors font-semibold text-sm disabled:opacity-60 w-full" data-testid="scribe-develop-btn">
                        {transforming === "develop" ? <Loader2 size={15} className="animate-spin" /> : <Expand size={15} />} {t("scribe.develop")}
                      </button>
                      <button onClick={doListen} className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-primary/40 text-primary hover:bg-primary/10 transition-colors font-semibold text-sm w-full" data-testid="scribe-listen-btn">
                        {audioState === "loading" ? <Loader2 size={15} className="animate-spin" /> : audioState === "playing" ? <Square size={15} /> : <Volume2 size={15} />}
                        {audioState === "playing" ? t("scribe.stopListen") : t("scribe.listen")}
                      </button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <button onClick={discuss} className="btn-frost-orange flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold text-sm w-full" data-testid="scribe-discuss-btn">
                        <MessageCircle size={15} /> {t("scribe.discuss")}
                      </button>
                      <button onClick={reset} className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-white/15 text-foreground/60 hover:text-foreground transition-colors font-semibold text-sm w-full" data-testid="scribe-again-btn">
                        <RotateCcw size={15} /> {t("scribe.again")}
                      </button>
                    </div>
                  </div>

                  <AnimatePresence>
                    {showLangs && (
                      <motion.div key="langs" initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
                        className="mt-4 flex flex-wrap items-center justify-center gap-2" data-testid="scribe-lang-picker">
                        <span className="text-foreground/40 text-xs mr-1">{t("scribe.translateInto")}</span>
                        {LANGS.map(([code, label]) => (
                          <button key={code} onClick={() => doTranslate(code)} data-testid={`scribe-lang-${code}`}
                            className="px-3.5 py-1.5 rounded-full border border-white/15 text-foreground/70 hover:text-primary hover:border-primary/40 transition-colors text-sm">
                            {label}
                          </button>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>

                  {remaining != null && !unlimited && (
                    <p className="text-foreground/40 text-xs mt-4" data-testid="scribe-remaining">{t("scribe.remaining", { count: remaining })}</p>
                  )}
                </motion.div>
              )}

              {status === "limit" && (
                <motion.div key="limit" initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="flex flex-col items-center" data-testid="scribe-limit">
                  <span className="w-14 h-14 rounded-full bg-primary/15 text-primary flex items-center justify-center mb-4"><Lock size={24} /></span>
                  <p className="text-foreground/75 max-w-md leading-relaxed mb-6">{t("scribe.limit")}</p>
                  <Link to="/entrer" className="btn-frost-orange inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold" data-testid="scribe-limit-cta">
                    <Sparkles size={17} /> {t("scribe.limitCta")}
                  </Link>
                </motion.div>
              )}

              {status === "error" && (
                <motion.div key="error" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="flex flex-col items-center" data-testid="scribe-error">
                  <p className="text-foreground/70 mb-5">{errMsg || t("scribe.error")}</p>
                  <button onClick={reset} className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-primary/30 text-primary hover:bg-primary/10 transition-colors font-semibold text-sm" data-testid="scribe-retry-btn">
                    <RotateCcw size={16} /> {t("scribe.again")}
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};
